import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';

// Helper function to get user-friendly error messages
const getAuthErrorMessage = (errorCode, t) => {
  const errorMessages = {
    'auth/email-already-in-use': t ? t('auth.emailAlreadyInUse') : '이미 등록된 이메일입니다. 로그인하거나 다른 이메일 주소를 사용해주세요.',
    'auth/invalid-email': t ? t('auth.invalidEmail') : '올바른 이메일 주소를 입력해주세요.',
    'auth/weak-password': t ? t('auth.weakPassword') : '비밀번호는 최소 6자 이상이어야 합니다.',
    'auth/wrong-password': t ? t('auth.wrongPassword') : '비밀번호가 일치하지 않습니다. 다시 시도해주세요.',
    'auth/user-not-found': t ? t('auth.userNotFound') : '해당 이메일로 등록된 계정이 없습니다. 먼저 회원가입해주세요.',
    'auth/too-many-requests': t ? t('auth.tooManyRequests') : '로그인 시도 횟수가 너무 많습니다. 나중에 다시 시도해주세요.',
    'auth/network-request-failed': t ? t('auth.networkError') : '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.'
  };

  return errorMessages[errorCode] || errorCode;
};

const authNotConfigured = { success: false, error: 'Firebase auth is not configured. Please set REACT_APP_FIREBASE_API_KEY.' };

export const signUpWithEmail = async (email, password, displayName) => {
  if (!auth) return authNotConfigured;
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('✅ User created:', userCredential.user.uid);

    // Update user profile with display name
    await updateProfile(userCredential.user, {
      displayName: displayName
    });
    console.log('✅ Profile updated with displayName:', displayName);

    // Send email verification
    await sendEmailVerification(userCredential.user);
    console.log('✅ Email verification sent to:', email);
    console.log('📧 Check your inbox (and spam folder) for verification email from: noreply@' + auth.app.options.projectId + '.firebaseapp.com');

    // Initialize user document with default "Magnificent 7" theme
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: email,
      displayName: displayName,
      watchlist: [],
      watchlistThemes: ['magnificent-7'], // Default theme for new users
      userRole: 'member',
      emailVerified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    console.log('✅ User document created in Firestore');

    return { success: true, user: userCredential.user, emailVerificationSent: true };
  } catch (error) {
    console.error('❌ Signup error:', error.code, error.message);
    return {
      success: false,
      error: getAuthErrorMessage(error.code),
      errorCode: error.code
    };
  }
};

export const signInWithEmail = async (email, password) => {
  if (!auth) return authNotConfigured;
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Check if email is verified
    if (!userCredential.user.emailVerified) {
      return {
        success: false,
        user: userCredential.user,
        emailNotVerified: true,
        error: 'Please verify your email address before signing in. Check your inbox for the verification link.'
      };
    }

    // Update Firestore if email is verified
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      emailVerified: true,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { success: true, user: userCredential.user };
  } catch (error) {
    return {
      success: false,
      error: getAuthErrorMessage(error.code),
      errorCode: error.code
    };
  }
};

export const resendVerificationEmail = async (user) => {
  if (!auth) return authNotConfigured;
  try {
    await sendEmailVerification(user);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: getAuthErrorMessage(error.code),
      errorCode: error.code
    };
  }
};

export const signInWithGoogle = async () => {
  if (!auth) return authNotConfigured;
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);

    // Initialize user document with default "Magnificent 7" theme for new Google users
    await setDoc(doc(db, 'users', result.user.uid), {
      email: result.user.email,
      displayName: result.user.displayName,
      watchlist: [],
      watchlistThemes: ['magnificent-7'], // Default theme for new users
      userRole: 'member',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { success: true, user: result.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const resetPassword = async (email) => {
  if (!auth) return authNotConfigured;
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: getAuthErrorMessage(error.code),
      errorCode: error.code
    };
  }
};

export const logout = async () => {
  if (!auth) return authNotConfigured;
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
